import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiFilm, FiRefreshCw, FiLoader } from 'react-icons/fi';
import { useAllTrailers } from '../hooks/useMovies';
import TrailerCard from '../components/TrailerCard';
import Modal from '../components/Modal';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../utils/videoUtils';

const TrailersPage: React.FC = () => {
  const { trailers, loading, error, hasMore, mainOnly, setMainOnly, loadMore, refresh } =
    useAllTrailers(20);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);

  const handlePlayClick = (videoUrl: string) => {
    setActiveVideoUrl(videoUrl);
    setIsModalOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Latest Movie Trailers - Next in Cinema</title>
        <meta
          name="description"
          content="Watch the latest movie trailers for upcoming and current cinema releases. Stay up to date with new trailers from all major studios."
        />
        <meta property="og:title" content="Latest Movie Trailers - Next in Cinema" />
        <meta
          property="og:description"
          content="Watch the latest movie trailers for upcoming and current cinema releases."
        />
        <meta property="og:url" content="https://www.nextincinema.com/trailers" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.nextincinema.com/trailers" />
      </Helmet>

      <main className="pt-20 pb-16">
        <div className="container-custom py-8 animate-fade-in">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <FiFilm className="w-7 h-7 text-accent-teal flex-shrink-0" />
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark leading-tight">
                  Movie Trailers
                </h1>
                <p className="text-secondary-light text-sm mt-1">
                  The latest trailers from upcoming and current releases
                </p>
              </div>
            </div>

            {/* Filter bar */}
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <button
                onClick={() => setMainOnly(false)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  !mainOnly
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-white text-secondary-dark border border-secondary/20 hover:border-primary/40'
                }`}
              >
                All Trailers
              </button>
              <button
                onClick={() => setMainOnly(true)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  mainOnly
                    ? 'bg-accent-orange text-white shadow-sm'
                    : 'bg-white text-secondary-dark border border-secondary/20 hover:border-accent-orange/40'
                }`}
              >
                Main Trailers
              </button>
            </div>
          </div>

          {/* Loading state — initial */}
          {loading && trailers.length === 0 && (
            <div className="flex items-center justify-center h-64">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin" />
                <div className="absolute inset-0 border-t-4 border-primary rounded-full" />
              </div>
            </div>
          )}

          {/* Error state */}
          {error && trailers.length === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-secondary-dark mb-4">
                Oops! We couldn't load the trailers at the moment.
              </p>
              <button
                onClick={refresh}
                className="inline-flex items-center space-x-2 btn-primary"
              >
                <FiRefreshCw className="w-5 h-5" />
                <span>Try Again</span>
              </button>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && trailers.length === 0 && (
            <div className="bg-background-light border border-primary/10 rounded-xl p-10 text-center">
              <FiFilm className="w-12 h-12 text-secondary-light mx-auto mb-4" />
              <p className="text-secondary-dark font-medium">
                {mainOnly ? 'No main trailers available yet.' : 'No trailers available yet.'}
              </p>
            </div>
          )}

          {/* Trailer grid */}
          {trailers.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {trailers.map((trailer) => (
                <div key={trailer._id} className="animate-slide-up">
                  <TrailerCard trailer={trailer} onPlayClick={handlePlayClick} />
                </div>
              ))}
            </div>
          )}

          {/* Load More button */}
          {trailers.length > 0 && (hasMore || loading) && (
            <div className="flex justify-center mt-10">
              <button
                onClick={loadMore}
                disabled={loading}
                className="inline-flex items-center gap-2 btn-primary px-8 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <FiLoader className="w-5 h-5 animate-spin" />
                    Loading…
                  </>
                ) : (
                  'Load More Trailers'
                )}
              </button>
            </div>
          )}

          {/* All loaded message */}
          {trailers.length > 0 && !hasMore && !loading && (
            <p className="text-center text-secondary-light text-sm mt-10">
              You've seen all {trailers.length} trailer{trailers.length !== 1 ? 's' : ''}.
            </p>
          )}
        </div>
      </main>

      {/* Video Modal */}
      {activeVideoUrl && isYouTubeUrl(activeVideoUrl) && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <iframe
            src={getYouTubeEmbedUrl(activeVideoUrl) + '?autoplay=1'}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Modal>
      )}
    </>
  );
};

export default TrailersPage;
